import { MediaAgent } from '@smarterservices/media-agent';
import { Room } from 'livekit-client';
import { create } from 'zustand';
import config from '../config';
import CONSTANTS from '../config/constants';
import ApiClient from '../lib/ApiClient';

export const createAppStore = (initialState = {}) =>
  create((set, get) => ({
    stores: {},
    initialized: false,
    sessionSid: null,
    apiUrl: null,
    roomId: null,
    spToken: null,
    identity: null,
    websocketUrl: null,
    iam: null,
    iamDefaultParts: {
      partition: 'ss',
      service: 'sp',
      region: '',
      account: '',
    },
    enabledComponents: config.defaultComponentState,
    mediaClient: null,
    ApiClient: null,
    room: null,
    isRoomConnected: false,
    openCheckinDrawer: false,
    unreadChatMessageCount: 0,
    participantSid: null,
    type: null,
    viewMode: 'focus', // valid options are focus or overview
    isMicrophoneShareActive: false,
    isCameraShareActive: false,
    isScreenShareActive: false,
    user: {
      firstName: null,
      lastName: null,
      email: null,
      username: null,
    },

    injectDependentStores: (stores) => {
      set({ stores });
    },

    storeState: (name) => {
      if (!get().stores[name])
        throw new Error(`${name} is not a valid dependency in this store.`);
      return get().stores[name].getState();
    },

    /**
     * Initialize the app store.
     *
     * This method initializes the app store by taking in options and
     * creating a new MediaAgent and Room instance. It sets up listeners
     * for participants and sets the initial state of the store.
     *
     * @param {Object} options - Options to initialize the store. Must include:
     *   - apiUrl: The URL of the API to use for the MediaAgent
     *   - roomId: The ID of the room to join
     *   - identity: The identity of the user to use when joining the room
     *   - spToken: The token to use when joining the room
     *   - websocketUrl: The URL of the WebSocket to use for the Room
     *   - enabledComponents: Optional. An object with boolean values to enable or disable features.
     *     - enabledComponents.checkin: Enable or disable the checkin feature.
     *     - enabledComponents.chat: Enable or disable the chat feature.
     *     - enabledComponents.recording: Enable or disable the recording feature.
     */
    initialize: async (options, props) => {
      const settings = { ...config.defaultComponentState, ...options };

      set({
        ApiClient: new ApiClient({ spToken: options.spToken }),
        spToken: options.spToken,
        sessionSid: options.sessionSid,
        type: options.type || 'instructor', //TODO: need to validate this...
        viewMode: options.viewMode || 'focus',
      });



      // call check to ensure the token is valid...
      const check = await get().ApiClient.checkAuthentication();
      if (!check.successful) {
        // TODO: Flip state so an error page is shown..
        throw new Error('Failed to retrieve permissions');
      }


      // before we do anything, let's check the permissions...
      const permissions = await get().ApiClient.permissions();
      if (!permissions.successful) {
        // TODO: Flip state so an error page is shown..
        throw new Error('Failed to retrieve permissions');
      }

      set({
        iam: permissions.data.iam,
        user: {
          firstName: check.data?.firstName,
          lastName: check.data?.lastName,
          email: check.data?.email,
          username: check.data?.username,
        },
        enabledComponents: {
          ...settings.enabledComponents,
          ...options.enabledComponents,
        },
      });

      get()
        .stores.playerStore.getState()
        .setActiveVideoSource(options?.video?.default || config.video.default);
      get()
        .stores.playerStore.getState()
        .setCanHaveLiveVideo(
          options?.video?.sources?.includes(CONSTANTS.VIDEO_SOURCES.LIVE) ||
          false
        );
      get()
        .stores.playerStore.getState()
        .setCanHaveRecordedVideo(
          options?.video?.sources?.includes(CONSTANTS.VIDEO_SOURCES.RECORDED) ||
          false
        );

      // initialize the session store to get started, this will ensure all session data is loaded...
      await get().stores.examSessionStore.getState().initialize({
        sessionSid: options.sessionSid,
        examSid: options.examSid,
        installSid: options.installSid,
      });

      set((state) => ({
        ...state,
        roomId: options.roomId,
        identity: options.identity,
        apiUrl: options.apiUrl,
        websocketUrl: options.websocketUrl,
        iamDefaultParts: {
          ...state.iamDefaultParts,
          account: options.installSid
        },
      }));

      // check to see if there should be live video...
      if (get().stores.examSessionStore.getState()?.canHaveLiveVideo) {
        // if a bundle was passed in, use that...
        // TODO: We will want to ensure the bundle is valid...
        if (options.connectionBundle) {
          set({
            mediaClient: options.connectionBundle.mediaClient,
            room: options.connectionBundle.room,
            roomId: options.connectionBundle.roomId,
            identity: options.connectionBundle.identity,
            apiUrl: options.connectionBundle.apiUrl,
            websocketUrl: options.connectionBundle.websocketUrl,
          });
        } else {
          await get().buildMediaConnection();
        }
      } else {
        // this session cannot have live video, meaning it is likely over...
        set({ apiUrl: options.apiUrl });
      }

      // ensure the player is all setup and ready to go...
      await get().stores.playerStore.getState().initialize();

      // register the online/offline events...
      //await get().stores.eventStore.getState().initialize(props);

      // Signal that the app has been completely initialized...
      set({ initialized: true });
    },

    buildMediaConnection: async () => {
      const clientConfig = {
        apiUrl: get().apiUrl,
        roomId: get().roomId,
        identity: get().identity,
        token: get().spToken,
        websocketUrl: get().websocketUrl,
        type: 'instructor',
      };

      const clientInstance = await MediaAgent(clientConfig);
      const roomInstance = new Room();
      const mediaClient = clientInstance;
      const room = roomInstance;

      set({
        mediaClient,
        room,
        roomId: get().roomId,
        identity: get().identity,
        apiUrl: get().apiUrl,
        websocketUrl: get().websocketUrl,
      });
    },

    getMediaConnectionBundle: () => {
      return {
        mediaClient: get().mediaClient,
        room: get().room,
        roomId: get().roomId,
        identity: get().identity,
        apiUrl: get().apiUrl,
        websocketUrl: get().websocketUrl,
      };
    },

    /**
     * Set the isRoomConnected state to the given value.
     *
     * This is used to update the app store when the connection to the room is
     * established or lost.
     *
     * @param {boolean} value - The value to set isRoomConnected to.
     */
    setIsRoomConnected: (value) => {
      set({ isRoomConnected: value });
    },

    /**
     * Set the unreadChatMessageCount state to the given value.
     *
     * This is used to update the app store when there are new unread messages
     * in the chat.
     *
     * @param {number} count - The number of unread messages.
     */
    setUnreadChatMessageCount: (count) => {
      set({ unreadChatMessageCount: count });
    },

    setIsMicrophoneShareActive: (value) => {
      set({ isMicrophoneShareActive: value });
    },

    setIsCameraShareActive: (value) => {
      set({ isCameraShareActive: value });
    },

    setIsScreenShareActive: (value) => {
      set({ isScreenShareActive: value });
    },

    getIamResourceString: (resource, overrides = {}) => {
      const defaultParts = get().iamDefaultParts;
      const finalPartition = overrides.partition || defaultParts.partition;
      const finalService = overrides.service || defaultParts.service;
      const finalRegion = overrides.region || defaultParts.region;
      const finalAccount = overrides.account || defaultParts.account;


      return `ssrn:${finalPartition}:${finalService}:${finalRegion}:${finalAccount}:${resource}`;
    },

  }));
